import { Component, OnInit, OnDestroy, OnChanges, Input } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { UserService } from '../../../../../auth/user.service';
import { PlinksDashboardService } from '../plinks-dashboard.service';

@Component({
  selector: 'app-plink-quick-view',
  templateUrl: './plink-quick-view.component.html',
  styleUrls: ['./plink-quick-view.component.css']
})
export class PlinkQuickViewComponent implements OnInit, OnDestroy, OnChanges {

  @Input() customerID: number = 0;
  @Input() siteID: number = 0;
  @Input() plink: any = {};
  @Input() hasPlinks = false;

  // Local device data - work with local copy to avoid affecting shared plinkInfo
  localDevice: any = {};
  deviceId: number = 0;

  // Image loading state
  imageLoadError: boolean = false;

  // Mock data properties
  duration: string = '00:00:00';
  kwhrs: number = 0.0;

  // Status box states (mock data) - default to 'off' state
  plinkAcIn: string = 'off';      // green, yellow, red, off
  plinkConnected: string = 'off';  // green, yellow, red, off
  plinkAcOut: string = 'off';     // green, yellow, red, off
  plinkFault: string = 'off';        // green, yellow, red, off

  // LED positions in SVG coordinates - optimized through experimentation
  ledPositions = {
    acIn: { x: 292, y: 180 },      // AC In LED - top
    connected: { x: 292, y: 254 }, // Connected LED - second
    acOut: { x: 292, y: 328 },     // AC Out LED - third
    fault: { x: 292, y: 402 }      // Fault LED - bottom
  };

  // Text positions in SVG coordinates - optimized placement
  textPositions = {
    duration: {
      labelX: 150, labelY: 490,
      valueX: 714, valueY: 277
    },
    kwh: {
      labelX: 350, labelY: 490,
      valueX: 718, valueY: 322
    }
  };

  // Timer management
  private timerInterval: any;
  private startTime: Date;
  private elapsedSeconds: number = 0;

  // Real data management (Stage 1)
  private dataFetchTimer: any;
  private realQuickViewData: any = null;

  // Active tab detection
  private isQuickViewTabActive: boolean = false;
  private routerSubscription: any;

  constructor(
    public userService: UserService,
    private plinksDashboardService: PlinksDashboardService,
    private router: Router
  ) { }

  ngOnInit() {
    this.initializeLocalDevice();
    this.startTimer();
    // DEMO MODE DISABLED - Comment out to re-enable mock data for demos
    // this.generateMockData();

    // Initialize active tab detection
    this.initializeActiveTabDetection();

    // Stage 1: Start real data fetching only if tab is active
    this.checkAndStartRealDataFetching();
  }

  ngOnChanges(changes) {
    // Handle device selection changes - work with local copy to avoid affecting shared plinkInfo
    if(changes.plink && changes.plink.currentValue) {
      this.initializeLocalDevice();
      // Device changed - reset timer
      this.stopTimer();
      this.startTimer();
      // DEMO MODE DISABLED - Comment out to re-enable mock data for demos
      // this.generateMockData();

      // Stage 1: Restart real data fetching for new device (only if tab is active)
      this.stopRealDataFetching();
      this.checkAndStartRealDataFetching();
    }
  }

  initializeLocalDevice() {
    // Create local copy of device data to avoid affecting shared plinkInfo object
    // Similar to how PolarLink Events component handles device data

    if(this.plink && this.plink.deviceid) {
      // Create a deep copy of the device object
      this.localDevice = JSON.parse(JSON.stringify(this.plink));

      // Extract device ID safely, handling both string and number formats
      const rawDeviceId = this.localDevice.deviceid || this.localDevice.id;
      this.deviceId = rawDeviceId ? parseInt(rawDeviceId.toString(), 10) : 0;

      // DEMO MODE DISABLED - Comment out to re-enable mock data for demos
      // Device selected - generate active mock data
      // this.generateMockData();
    } else {
      this.localDevice = {};
      this.deviceId = 0;

      // No device selected - set inactive states
      this.setInactiveStates();
    }
  }

  setInactiveStates() {
    // Set all status boxes to off state (not lit)
    this.plinkAcIn = 'off';
    this.plinkConnected = 'off';
    this.plinkAcOut = 'off';
    this.plinkFault = 'off';

    // Reset timer and kWh to zero
    this.duration = '00:00:00';
    this.kwhrs = 0.0;
    this.elapsedSeconds = 0;
  }

  ngOnDestroy() {
    this.stopTimer();

    // Stage 1: Clean up real data fetching timer
    if (this.dataFetchTimer) {
      clearInterval(this.dataFetchTimer);
    }

    // Clean up router subscription
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  startTimer() {
    this.startTime = new Date();
    this.elapsedSeconds = 0;

    this.timerInterval = setInterval(() => {
      this.elapsedSeconds++;
      this.updateDuration();
      // DEMO MODE DISABLED - Comment out to re-enable mock data updates for demos
      // this.updateMockData();
    }, 1000);
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  updateDuration() {
    // PRODUCTION MODE: Only show duration if we have real data from device
    // Without real data, keep duration at 00:00:00 (don't use mock timer)
    if (!this.realQuickViewData ||
        this.realQuickViewData.eventDuration === undefined ||
        this.realQuickViewData.eventDuration === null) {
      // No real data - keep at 00:00:00
      this.duration = '00:00:00';
    }
    // If we have real data, updateDurationFromRealData() will be called separately
  }

  updateDurationFromRealData(eventDurationSeconds: number) {
    // Convert seconds to HH:MM:SS format
    const hours = Math.floor(eventDurationSeconds / 3600);
    const minutes = Math.floor((eventDurationSeconds % 3600) / 60);
    const seconds = eventDurationSeconds % 60;

    this.duration = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    // DEMO MODE DISABLED - Comment out to re-enable console logs for demos
    // console.log('✅ Stage 3: Updated duration from real data:', this.duration, 'from', eventDurationSeconds, 'seconds');
  }

  // DEMO MODE DISABLED - Comment out to re-enable mock data generation for demos
  // Keep these methods for future demo builds
  generateMockData() {
    // Generate initial mock kWh value
    this.kwhrs = Math.random() * 10;

    // Generate random status states for demo
    const states = ['green', 'yellow', 'red'];
    this.plinkAcIn = states[Math.floor(Math.random() * states.length)];
    this.plinkConnected = states[Math.floor(Math.random() * states.length)];
    this.plinkAcOut = states[Math.floor(Math.random() * states.length)];
    this.plinkFault = states[Math.floor(Math.random() * states.length)];
  }

  updateMockData() {
    // Stage 2: Don't update kWh if we have real data
    if (!this.realQuickViewData || this.realQuickViewData.totalBillableEnergykWh === undefined) {
      // Slowly increment kWh value over time (mock data only)
      this.kwhrs += Math.random() * 0.01;
    }

    // Stage 4: Don't update LED states if we have real LED data
    if (!this.realQuickViewData || this.realQuickViewData.rseLedStatus === undefined) {
      // Occasionally change status states for demo purposes (mock data only)
      if (this.elapsedSeconds % 10 === 0) {
        const states = ['green', 'yellow', 'red', 'off'];
        if (Math.random() > 0.7) {
          this.plinkAcIn = states[Math.floor(Math.random() * states.length)];
        }
        if (Math.random() > 0.7) {
          this.plinkConnected = states[Math.floor(Math.random() * states.length)];
        }
        if (Math.random() > 0.7) {
          this.plinkAcOut = states[Math.floor(Math.random() * states.length)];
        }
        if (Math.random() > 0.7) {
          this.plinkFault = states[Math.floor(Math.random() * states.length)];
        }
      }
    }
  }

  getStatusClass(status: string): string {
    return `status-${status}`;
  }

  onImageError(event: any) {
    console.error('QuickView: Image failed to load:', event);
    this.imageLoadError = true;
  }

  onImageLoad(event: any) {
    this.imageLoadError = false;
  }

  // SVG LED color mapping
  getStatusColor(status: string): string {
    switch(status) {
      // DEMO MODE DISABLED - green/yellow colors were for mock data demos
      case 'green': return '#00FF00';   // Bright green (mock data - for demos only)
      case 'yellow': return '#FFFF00';  // Bright yellow (mock data - for demos only)
      case 'red': return '#FF0000';     // Bright red (fault or mock data)
      case 'lightblue': return '#66FFFF'; // Light blue (real data ON status)
      case 'green': return '#1DB650';
      case 'off': return '#404040';     // Dark gray when off/inactive
      default: return '#404040';        // Default to off state
    }
  }

  // SVG LED stroke color (border)
  getStatusStroke(status: string): string {
    switch(status) {
      // DEMO MODE DISABLED - green/yellow colors were for mock data demos
      case 'green': return '#00CC00';   // Darker green border (mock data - for demos only)
      case 'yellow': return '#CCCC00';  // Darker yellow border (mock data - for demos only)
      case 'red': return '#CC0000';     // Darker red border (fault or mock data)
      case 'lightblue': return '#33CCCC'; // Darker light blue border (real data)
      case 'off': return '#666666';     // Gray border when off
      default: return '#666666';        // Default gray border
    }
  }

  // ===== ACTIVE TAB DETECTION METHODS =====

  /**
   * Initialize active tab detection using router events
   * Only fetch QuickView data when user is on the QuickView tab
   */
  initializeActiveTabDetection() {
    // Check initial route
    this.checkIfQuickViewTabActive();

    // Subscribe to router events to detect tab changes
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const wasActive = this.isQuickViewTabActive;
        this.checkIfQuickViewTabActive();

        // If tab became active, start data fetching
        if (!wasActive && this.isQuickViewTabActive) {
          this.checkAndStartRealDataFetching();
        }
        // If tab became inactive, stop data fetching
        else if (wasActive && !this.isQuickViewTabActive) {
          this.stopRealDataFetching();
        }
      });
  }

  /**
   * Check if the current route is the QuickView tab
   */
  checkIfQuickViewTabActive() {
    const currentUrl = this.router.url;
    this.isQuickViewTabActive = currentUrl.includes('/quick-view/');
  }

  /**
   * Start real data fetching only if QuickView tab is active and user is logged in
   */
  checkAndStartRealDataFetching() {
    if (this.isQuickViewTabActive && this.userService.getCurrentUser()?.id) {
      this.startRealDataFetching();
    }
  }

  // ===== STAGE 1: REAL DATA METHODS =====

  /**
   * Start real data fetching every 15 seconds
   * Stage 1: Establish data pipeline alongside mock data
   * Updated: Only runs when QuickView tab is active
   */
  startRealDataFetching() {
    if (this.deviceId > 0) {
      // Fetch initial data immediately
      this.fetchRealQuickViewData();

      // Set up 15-second interval for real data updates
      this.dataFetchTimer = setInterval(() => {
        // Double-check tab is still active before fetching
        if (this.isQuickViewTabActive) {
          this.fetchRealQuickViewData();
        } else {
          this.stopRealDataFetching();
        }
      }, 15000); // 15 seconds as specified
    }
  }

  /**
   * Fetch real QuickView data using PolarLink command
   * Stage 2: Get real data and replace mock energy values
   */
  fetchRealQuickViewData() {
    if (this.deviceId <= 0) {
      return;
    }

    // Stage 2: Get QuickView response data (includes queuing command + getting response)
    this.plinksDashboardService.getPolarLinkQuickView(this.deviceId, false)
      .subscribe({
        next: (response: any) => {
          // Stage 2: Process real data
          this.processRealQuickViewData(response);
        },
        error: (error) => {
          // DEMO MODE DISABLED - Comment out to re-enable error logging for demos
          // console.error('❌ Stage 2: Error getting QuickView data:', error);

          // Fallback: Still queue the command for future responses
          this.plinksDashboardService.addCommandToPlinkQueue(this.deviceId, 'quickView', {})
            .subscribe({
              next: (queueResponse) => {
                // Command queued successfully
              },
              error: (queueError) => {
                // DEMO MODE DISABLED - Comment out to re-enable error logging for demos
                // console.error('❌ Stage 2: Error queuing QuickView command:', queueError);
              }
            });
        }
      });
  }

  /**
   * Process real QuickView data from device
   * Stage 2: Replace mock energy data with real values
   */
  processRealQuickViewData(response: any) {
    try {
      if (response && response.QV && Object.keys(response.QV).length > 0) {
        const realData = response.QV;

        // Stage 2: Replace mock energy data with real data
        if (realData.totalBillableEnergykWh !== undefined) {
          this.realQuickViewData = realData;

          // Update the displayed energy value
          this.kwhrs = realData.totalBillableEnergykWh.toFixed(2);
        }

        // Stage 4: Process LED status data from RseLedStatus bit field
        if (realData.rseLedStatus !== undefined) {
          this.processLedStatus(realData.rseLedStatus);
        } else if (this.realQuickViewData) {
          // If we have real data but no LED status, still process fault LED
          this.processFaultLedStatus();
        }

        // Stage 3: Replace duration data with real eventDuration
        if (realData.eventDuration !== undefined && realData.eventDuration !== null) {
          // Only set realQuickViewData for duration if we have valid duration data
          if (!this.realQuickViewData) {
            this.realQuickViewData = {};
          }
          this.realQuickViewData.eventDuration = realData.eventDuration;
          this.updateDurationFromRealData(realData.eventDuration);
        }
      }
    } catch (ex) {
      // DEMO MODE DISABLED - Comment out to re-enable error logging for demos
      // console.error('❌ Stage 2: Error processing QuickView data:', ex);
    }
  }

  /**
   * Stop real data fetching
   * Stage 1: Clean up timers
   */
  stopRealDataFetching() {
    if (this.dataFetchTimer) {
      clearInterval(this.dataFetchTimer);
      this.dataFetchTimer = null;
    }
  }

  /**
   * Process LED status from RseLedStatus bit field (Stage 4)
   * Index 23: RseLedStatus (unsigned char, size 1)
   * Bit mapping: bit0=ACIN | bit1=CONNECTED | bit2=ACOUT | bit3-bit7 Reserved
   * Values: 0=OFF, 1=ON
   * @param {number} rseLedStatus - The LED status bit field from device
   */
  processLedStatus(rseLedStatus: number) {
    // DEMO MODE DISABLED - Comment out to re-enable debug logging for demos
    // console.log('🔧 Stage 4: Processing LED status from RseLedStatus:', rseLedStatus, '(binary:', rseLedStatus.toString(2).padStart(8, '0'), ')');

    // Extract individual LED states using bit manipulation
    const acInStatus = (rseLedStatus & 0x01) !== 0;      // bit0 = ACIN
    const connectedStatus = (rseLedStatus & 0x02) !== 0; // bit1 = CONNECTED
    const acOutStatus = (rseLedStatus & 0x04) !== 0;     // bit2 = ACOUT
    // bit3-bit7 are reserved

    // Update LED states based on bit values - use light blue for real data ON status
    this.plinkAcIn = acInStatus ? 'green' : 'off';
    this.plinkConnected = connectedStatus ? 'lightblue' : 'off';
    this.plinkAcOut = acOutStatus ? 'lightblue' : 'off';

    // Process Fault LED separately using faultRelayCode bit field
    this.processFaultLedStatus();
  }

  /**
   * Process Fault LED status from faultRelayCode bit field (Stage 4)
   * unsigned char faultRelayCode; (0=OFF,1=ON) bit0=Relay AUX bad | bit1-bit7 Reserved
   */
  processFaultLedStatus() {
    if (!this.realQuickViewData) {
      return;
    }

    const faultRelayCode = this.realQuickViewData.faultRelayCode || 0;
    const faultErrorCode = this.realQuickViewData.faultErrorCode || 0;

    // DEMO MODE DISABLED - Comment out to re-enable debug logging for demos
    // console.log('🔧 Stage 4: Processing Fault LED status:', {
    //   faultRelayCode: faultRelayCode,
    //   faultErrorCode: faultErrorCode,
    //   faultRelayBinary: faultRelayCode.toString(2).padStart(8, '0')
    // });

    // Extract fault status using bit manipulation
    const relayAuxBad = (faultRelayCode & 0x01) !== 0;  // bit0 = Relay AUX bad
    // bit1-bit7 are reserved

    // Fault LED logic: Show RED if any fault is detected, OFF otherwise
    const hasFault = relayAuxBad || faultErrorCode > 0;
    this.plinkFault = hasFault ? 'red' : 'off';


  }
}
